import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { TherapiesKeys } from "./therapies.keys";
import { useApi } from "../../api/ApiProvider";
import type { CatalogUsageDto, TherapyType } from "../../api-client";

export const useTherapiesAdmin = (therapyType?: TherapyType, enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.therapies.all(therapyType),
    queryFn: () => masterlists.getTherapiesAdmin(therapyType),
    enabled,
    staleTime: 2 * 60 * 1000,
    meta: { errorMessage: "Therapien konnten nicht geladen werden" },
  });
};

export const useAllPrices = (enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.prices.all(),
    queryFn: () => masterlists.getAllPrices(),
    enabled,
    staleTime: 2 * 60 * 1000,
    meta: { errorMessage: "Preise konnten nicht geladen werden" },
  });
};

export const useDiagnosisGroupsAdmin = (therapyType?: TherapyType, enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.diagnosisGroups.all(therapyType),
    queryFn: () => masterlists.getDiagnosisGroupsAdmin(therapyType),
    enabled,
    staleTime: 2 * 60 * 1000,
    meta: { errorMessage: "Diagnosegruppen konnten nicht geladen werden" },
  });
};

export const useTherapyCertifications = (enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.therapyCertifications.all(),
    queryFn: () => masterlists.getTherapyCertifications(),
    enabled,
    staleTime: 30 * 60 * 1000,
    meta: { errorMessage: "Zertifizierungen konnten nicht geladen werden" },
  });
};

export const useTarifGroups = (enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.tarifGroups.all(),
    queryFn: () => masterlists.getTarifGroups(),
    enabled,
    staleTime: 30 * 60 * 1000,
    meta: { errorMessage: "Tarifgruppen konnten nicht geladen werden" },
  });
};

export const useRxTypes = (enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.rxTypes.all(),
    queryFn: () => masterlists.getRxTypes(),
    enabled,
    staleTime: 30 * 60 * 1000,
    meta: { errorMessage: "Rezepttypen konnten nicht geladen werden" },
  });
};

/* ----------------------------- MD-232 catalog ----------------------------- */

/**
 * Prescribed TERMS with their aliases, prioritised resolutions and usage counts.
 * `staleTime` is short because the admin screen edits these and expects to see
 * the effect on the resolution chain immediately.
 */
export const useCatalogTerms = (therapyType?: TherapyType, enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.catalogTerms.all(therapyType),
    queryFn: () => masterlists.getCatalogTerms(therapyType),
    enabled,
    staleTime: 30 * 1000,
    meta: { errorMessage: "Heilmittel-Begriffe konnten nicht geladen werden" },
  });
};

/** Tarif-agnostic methods, each with the billable rows implementing it. */
export const useCatalogMethods = (therapyType?: TherapyType, enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.catalogMethods.all(therapyType),
    queryFn: () => masterlists.getCatalogMethods(therapyType),
    enabled,
    staleTime: 30 * 1000,
    meta: { errorMessage: "Methoden konnten nicht geladen werden" },
  });
};

/** The whole term -> method -> line item chain, plus structural issues. */
export const useCatalogGraph = (therapyType?: TherapyType, enabled = true) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.catalogGraph.all(therapyType),
    queryFn: () => masterlists.getCatalogGraph(therapyType),
    enabled,
    staleTime: 30 * 1000,
    meta: { errorMessage: "Katalog konnte nicht geladen werden" },
  });
};

/** What references a term — fetched on demand before offering a delete. */
export const useCatalogTermUsage = (id: string | null) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.catalogTerms.usage(id ?? ""),
    queryFn: () => masterlists.getCatalogTermUsage(id as string),
    enabled: !!id,
    meta: { errorMessage: "Verwendung konnte nicht geprüft werden" },
  });
};

/** What references a method — fetched on demand before offering a delete. */
export const useCatalogMethodUsage = (id: string | null) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: TherapiesKeys.catalogMethods.usage(id ?? ""),
    queryFn: () => masterlists.getCatalogMethodUsage(id as string),
    enabled: !!id,
    meta: { errorMessage: "Verwendung konnte nicht geprüft werden" },
  });
};

export type UsageKind = "therapy" | "price" | "diagnosisGroup";

/**
 * Ask what references a row at the moment the admin clicks delete.
 *
 * The catalog tabs (Methoden, Begriffe) derive their blockers from the list
 * payload, which already carries the counts. These three cannot: usage means
 * slots, prescriptions and price windows, which the admin lists deliberately
 * do not join. Asking per row on render would be one request per line; asking
 * on click is one request per intent, and it returns exactly the answer the
 * server is about to enforce — so the dialog can name the blockers instead of
 * offering a delete that comes back as a red toast.
 *
 * `staleTime: 0` because the whole point is freshness: a slot booked while the
 * screen was open must show up.
 */
export const useUsageCheck = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();

  return useCallback(
    (kind: UsageKind, id: string): Promise<CatalogUsageDto> => {
      const lookup = {
        therapy: {
          queryKey: TherapiesKeys.therapies.usage(id),
          queryFn: () => masterlists.getTherapyUsage(id),
        },
        price: {
          queryKey: TherapiesKeys.prices.usage(id),
          queryFn: () => masterlists.getPriceUsage(id),
        },
        diagnosisGroup: {
          queryKey: TherapiesKeys.diagnosisGroups.usage(id),
          queryFn: () => masterlists.getDiagnosisGroupUsage(id),
        },
      }[kind];

      return qc.fetchQuery({ ...lookup, staleTime: 0 });
    },
    [qc, masterlists]
  );
};
